import {
  format,
  parseISO,
  isValid,
  formatDistanceStrict,
  differenceInDays,
  getISODay,
  isSameDay,
  addMinutes,
  formatISO,
  addDays,
  intervalToDuration,
  eachDayOfInterval,
} from 'date-fns'
import nb from 'date-fns/locale/nb'
import { isDate } from 'lodash'

export function formatDate(date, dateFormat = 'd. MMMM', fallback = '—') {
  if (!date || !new Date(date)) {
    return fallback
  }

  const parsed = parseDate(date)

  return format(new Date(parsed), dateFormat, { locale: nb })
}

export function formatDateClean(date, dateFormat = 'd. MMMM yyyy', fallback = '—') {
  if (!date) {
    return fallback
  }

  return format(date, dateFormat, { locale: nb })
}

// Pretty format a date time range, ex "12. februar 10:00 - 15:30"
export function formatDateTimeRange(dateStart, dateEnd) {
  if (!dateStart || !dateEnd) {
    return 'Ugyldig dato'
  }

  const from = formatDateClean(dateStart, 'd. MMMM yyyy HH:mm')

  // If same day
  if (isSameDay(dateStart, dateEnd)) {
    return `${from} til ${formatDateClean(dateEnd, 'HH:mm')}`
  }

  return `${from} til ${formatDateClean(dateEnd, 'd. MMMM yyyy HH:mm')}`
}

export function parseDate(date) {
  if (!date || !new Date(date)) {
    return null
  }

  /**
   * If the date is an actual date instance, no need to create a new date. But it
   * is a string, do a replace on strings that ends with a + and just two digits –
   * it's a case of a malformed date string from the backend and needs to be fixed
   * it can be cast to a Date  instance
   */
  const value = isDate(date) ? date : new Date(date.replace(/\+(\d{2})$/, '+$100'))

  if (!isValid(value)) {
    console.warn("Date isn't valid", date)
    if (isValid(parseISO(date))) {
      console.warn('Formated', formatISO(date))
      return formatISO(date)
    }

    return null
  }

  // const isoDateTime = new Date(
  //   value.getTime() - value.getTimezoneOffset() * 60000,
  // ).toISOString()

  const dateOnly = new Date(value.getFullYear(), value.getMonth(), value.getDate())

  const isoDateTime = new Date(
    dateOnly.getTime() - dateOnly.getTimezoneOffset() * 60000,
  ).toISOString()

  // TODO: This is the correct way to store dates if the API can handle timezones correctly.
  // const isoDateTime = new Date(value).toISOString()
  return isoDateTime
}

export function daysBetween(from, to) {
  if (!from || !to || !new Date(from) || !new Date(to)) {
    return null
  }
  return differenceInDays(new Date(from), new Date(to))
}

export function distance(firstDate, secondDate) {
  return formatDistanceStrict(firstDate, secondDate, {
    addSuffix: true,
    locale: nb,
    // roundingMethod: 'ceil',
  })
}

/**
 * Calculate the number of days between two dates, including the start and end dates
 * but excluding the specified week days (if any)
 *
 * @param Date start
 * @param Date end
 * @param number[] excludedDays Array of days to exclude, the values being the numeric representation of the weekday 0=mon, ..., 6=sun
 */
export function getDaysBetweenDates(start, end, excludedDays = [], excludedDates = []) {
  if (start - end === 0) {
    return 0
  }

  try {
    return eachDayOfInterval({ start, end }).reduce((res, date) => {
      // the date is one of the excluded dates
      if (excludedDates.find((d) => isSameDay(d, date))) {
        return res
      }

      // the date is one of the excluded weekdays
      if (excludedDays.includes(getISODay(date) - 1)) {
        return res
      }

      // this day is not excluded
      return res + 1
    }, 0)
  } catch (e) {
    return []
  }
}

/**
 * Generate time slots based on start and end time and slot length
 *
 * @param {*} start
 * @param {*} end
 * @param {*} slotLength
 */
export function generateTimeslots(start, end, slotLength) {
  if (!start || !end || !slotLength) {
    return []
  }

  const startTime = new Date(`2020-01-01T${start}`)
  const endTime = new Date(`2020-01-01T${end}`)

  let time = startTime
  const slots = []

  while (time < endTime) {
    const slotEnd = addMinutes(time, slotLength)

    if (slotEnd > endTime) {
      time = slotEnd
      continue
    }

    slots.push({ time, label: format(time, 'HH:mm') })
    time = slotEnd
  }

  return slots
}

// Generate a list of dates (not datetime) for use in a select list
export function generateListOfDates({ startDate = new Date(), numberOfDays = 0 }) {
  const dates = [{ value: '', label: 'Velg dag' }]

  for (let i = 0; i < numberOfDays; i++) {
    const currentDate = addDays(new Date(startDate), i)
    const dateOnly = format(currentDate, 'yyyy-LL-dd', { locale: nb })
    const prettyDate = format(currentDate, 'EEEE d. MMMM', { locale: nb })

    dates.push({
      value: dateOnly, // Ex: "2021-09-24"
      label: prettyDate.charAt(0).toUpperCase() + prettyDate.slice(1), // Ex: "Fredag 24. september"
    })
  }

  return dates
}

// Generate a date (not datetime) x days from now
export function generateSingleDate({ numberOfDaysFromNow = 0 }) {
  const futureDate = addDays(new Date(), numberOfDaysFromNow)
  const formattedDate = format(futureDate, 'yyyy-LL-dd', { locale: nb })

  return formattedDate
}

// Return age, ex: "58"
export function getAge(birthDate) {
  if (birthDate == null) {
    return
  }

  const age = intervalToDuration({
    start: new Date(),
    end: new Date(birthDate),
  })

  return age.years
}
